import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { MenuItem } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import StyledTextField from './StyledTextField'

const UNITS = {
	days: 'days',
	hours: 'hours'
}

const LABELS = {
	[UNITS.days]: 'Days before',
	[UNITS.hours]: 'Hours before'
}

const useStyles = makeStyles(() => ({
	root: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'end',
		gap: '1rem',
		'& > div': {
			flex: 1
		}
	}
}))

/**
 * Allows for choosing between days before or hours before.
 *
 * Note: initialHours is passed in as an object to force a re-render when the value changes.
 *
 * Hours is passed in, but is displayed as days if it is greater than 48 (default)
 * If the hours is evenly divisible by 24, then it is displayed as days, otherwise it is displayed as hours
 *
 * Returns hours and the selected unit. However hours will always be the value in hours no matter what the selected unit is.
 *
 * viewonly is passed to the StyledTextField to render the value in a view only container
 * @param {{initialHours: {hours: number}, viewonly?: boolean, displayAsDaysThreshold?: Number, onUpdated: (e: {hours: Number, unit: string}) => any}} param0
 * @returns {{hours: number, unit: string}}
 */
const DaysOrHoursBeforeEntry = ({
	initialHours = { hours: 0 },
	displayAsDaysThreshold = 48,
	onUpdated,
	header = '',
	viewonly = false
}) => {
	const classes = useStyles()
	const [selectedTimeUnit, setSelectedTimeUnit] = useState('')
	const [timeValue, setTimeValue] = useState('')

	useEffect(() => {
		if (initialHours.hours > displayAsDaysThreshold && initialHours.hours % 24 === 0) {
			setSelectedTimeUnit(UNITS.days)
			setTimeValue(initialHours.hours / 24)
		} else {
			setSelectedTimeUnit(UNITS.hours)
			setTimeValue(initialHours.hours)
		}
	}, [initialHours, displayAsDaysThreshold])

	const handleOnUpdated = useCallback(
		({ hours, unit }) => {
			if (onUpdated) {
				onUpdated({ hours, unit })
			}
		},
		[onUpdated]
	)

	const handleTimeUnitChange = (e) => {
		const unit = e.target.value
		let hours = 0
		// we keep update values in hours always
		if (unit === UNITS.days) {
			hours = timeValue * 24
		} else {
			hours = timeValue
		}
		setSelectedTimeUnit(unit)
		handleOnUpdated({ hours, unit })
	}

	const handleTimeEntryChange = (e) => {
		if (e.target.value === '') {
			setTimeValue('')
			handleOnUpdated({ hours: 0, unit: selectedTimeUnit })
			return
		}
		const value = parseInt(e.target.value, 10)
		let hours = value
		// we keep update values in hours always
		if (selectedTimeUnit === UNITS.days) {
			hours = value * 24
		}
		setTimeValue(value)
		handleOnUpdated({ hours, unit: selectedTimeUnit })
	}

	const timeEntryLabel = useMemo(() => {
		return selectedTimeUnit === UNITS.days
			? 'Number of Days'
			: selectedTimeUnit === UNITS.hours
			? 'Number of Hours'
			: 'Days/Hours'
	}, [selectedTimeUnit])

	return (
		<div className={classes.root}>
			<div>
				<StyledTextField
					header={header}
					viewonly={viewonly}
					label={timeEntryLabel}
					variant="filled"
					value={timeValue}
					onChange={handleTimeEntryChange}
				/>
			</div>
			<div>
				<StyledTextField
					label="Time Unit"
					variant="filled"
					viewonly={viewonly}
					select
					value={viewonly ? LABELS[selectedTimeUnit] : selectedTimeUnit}
					onChange={handleTimeUnitChange}>
					{Object.entries(LABELS).map(([value, label]) => {
						return (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						)
					})}
				</StyledTextField>
			</div>
		</div>
	)
}

export default DaysOrHoursBeforeEntry
